import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, FileText, Loader2, Clock, Award, ChevronDown, ChevronUp, 
  RefreshCw, Upload, Camera, X, AlertCircle, Target, TrendingUp,
  Zap, BookOpen, CheckCircle, Play
} from 'lucide-react';

const subjectIcons = { 
  Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', 
  Hindi: '📚', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' 
};

const MAX_REGENERATIONS = 10;

export default function MockExamPage({ params, onNavigate }) {
  const { api, user } = useAuth();
  const [subject, setSubject] = useState(params?.subject || 'Math');
  const [sampleText, setSampleText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [exam, setExam] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedQ, setExpandedQ] = useState(null);
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [regenerateCount, setRegenerateCount] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [usedQuestionHashes, setUsedQuestionHashes] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const fileInputRef = useRef(null);

  const className = user?.class_name || 'Class 10';
  const subjects = ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'Physics', 'Chemistry', 'Biology'];

  // Target and current scores (mock data - would come from API)
  const targetScore = 85;
  const currentScore = 72;
  const gap = targetScore - currentScore;

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      alert('Please upload an image or PDF file');
      return;
    }

    setUploading(true);
    setUploadedFile(file);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/ocr/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.extracted_text) {
        setSampleText(res.data.extracted_text);
      }
    } catch (err) {
      console.error('OCR failed:', err);
      alert('Failed to extract text. Please paste text manually.');
      setUploadedFile(null);
    }
    setUploading(false);
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
    setSampleText('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const generateExam = async (isRegenerate = false) => {
    if (isRegenerate && regenerateCount >= MAX_REGENERATIONS) {
      alert(`Maximum regeneration limit (${MAX_REGENERATIONS}) reached.`);
      return;
    }

    setGenerating(true);
    if (!isRegenerate) {
      setExam(null);
      setRegenerateCount(0);
      setUsedQuestionHashes([]);
      setMcqAnswers({});
      setShowResults(false);
    }

    try {
      const res = await api.post('/ai/generate-mock-exam', {
        subject,
        class_name: className,
        sample_text: sampleText || null,
        num_mcq: 10,
        num_short: 10,
        num_long: 5,
        exclude_questions: usedQuestionHashes,
        regenerate_attempt: isRegenerate ? regenerateCount + 1 : 0,
      });
      
      if (res.data.exam?.sections) {
        const newHashes = [];
        res.data.exam.sections.forEach(section => {
          section.questions?.forEach(q => {
            newHashes.push(q.question?.substring(0, 50) || '');
          });
        });
        setUsedQuestionHashes(prev => [...prev, ...newHashes]);
      }

      setExam(res.data);
      if (isRegenerate) {
        setRegenerateCount(prev => prev + 1);
        setMcqAnswers({});
        setExpandedSection(null);
        setExpandedQ(null);
      }
    } catch (err) { 
      console.error(err); 
      alert('Failed to generate exam. Please try again.');
    }
    setGenerating(false);
  };

  const startNewExam = () => {
    setExam(null);
    setRegenerateCount(0);
    setUsedQuestionHashes([]);
    setMcqAnswers({});
    setExpandedSection(null);
    setExpandedQ(null);
    setSampleText('');
    setUploadedFile(null);
    setShowResults(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const remainingRegenerations = MAX_REGENERATIONS - regenerateCount;

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="mock-exam-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button 
            data-testid="back-from-mock" 
            onClick={() => onNavigate('dashboard')} 
            className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-all"
          >
            <ArrowLeft className="w-4 h-4 text-slate-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white" data-testid="mock-title">Mock Exam</h1>
            <p className="text-xs text-slate-500">{className} • Board Pattern</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-5 max-w-lg mx-auto">
        {/* Target vs Current Card (shown before exam) */}
        {!exam && (
          <>
            <div className="bg-gradient-to-br from-violet-600/20 to-cyan-600/10 border border-violet-500/20 rounded-2xl p-5 mb-6">
              <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Target className="w-4 h-4 text-violet-400" />
                Your Goal
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Target</p>
                  <p className="text-3xl font-bold text-violet-400">{targetScore}%</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Current Avg</p>
                  <p className="text-3xl font-bold text-white">{currentScore}%</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-xl">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-slate-300">Gap to close</span>
                </div>
                <span className="text-sm font-bold text-amber-400">+{gap}%</span>
              </div>
            </div>

            {/* Exam Configuration */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 space-y-5">
              {/* Subject Selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Subject</label>
                <div className="grid grid-cols-4 gap-2">
                  {subjects.slice(0, 8).map(s => (
                    <button
                      key={s}
                      onClick={() => setSubject(s)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                        subject === s
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
                      }`}
                      data-testid={`subject-${s.toLowerCase()}`}
                    >
                      <span className="text-lg">{subjectIcons[s] || '📘'}</span>
                      <span className={`text-[10px] ${subject === s ? 'text-violet-300' : 'text-slate-500'}`}>
                        {s.split(' ')[0]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Upload Section */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Sample Paper (Optional)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="mock-file-input"
                />
                
                {!uploadedFile ? (
                  <button
                    data-testid="mock-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full bg-slate-800/50 border-2 border-dashed border-slate-700 hover:border-violet-500/50 rounded-xl p-4 transition-all"
                  >
                    {uploading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
                        <span className="text-sm text-slate-400">Extracting text...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                          <Upload className="w-5 h-5 text-violet-400" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm text-white">Upload sample paper</p>
                          <p className="text-xs text-slate-500">Image or PDF</p>
                        </div>
                      </div>
                    )}
                  </button>
                ) : (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-emerald-300 truncate">{uploadedFile.name}</p>
                      <p className="text-xs text-emerald-400/70">Text extracted</p>
                    </div>
                    <button onClick={removeUploadedFile} className="text-slate-400 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Or paste text */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Or paste syllabus text
                </label>
                <textarea
                  data-testid="mock-sample-text"
                  value={sampleText}
                  onChange={e => setSampleText(e.target.value)}
                  placeholder="Paste chapter topics or previous paper text..."
                  rows={3}
                  className="w-full bg-slate-800/50 border border-slate-700 focus:border-violet-500 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none resize-none"
                />
              </div>

              {/* Exam Structure */}
              <div className="bg-slate-800/30 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-3 font-medium">EXAM STRUCTURE</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg font-bold text-violet-400">10</span>
                    </div>
                    <p className="text-xs text-slate-400">MCQ</p>
                    <p className="text-[10px] text-slate-600">1 mark each</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg font-bold text-cyan-400">10</span>
                    </div>
                    <p className="text-xs text-slate-400">Short</p>
                    <p className="text-[10px] text-slate-600">3 marks each</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg font-bold text-amber-400">5</span>
                    </div>
                    <p className="text-xs text-slate-400">Long</p>
                    <p className="text-[10px] text-slate-600">5 marks each</p>
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <button
                data-testid="generate-mock-btn"
                onClick={() => generateExam(false)}
                disabled={generating}
                className="w-full bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 disabled:from-slate-700 disabled:to-slate-700 text-white rounded-xl py-4 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Generating Exam...</>
                ) : (
                  <><Play className="w-5 h-5" /> Generate Mock Exam</>
                )}
              </button>
            </div>
          </>
        )}

        {/* Exam Paper */}
        {exam && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="mock-exam-paper">
            {/* Exam Header */}
            <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/50 border border-slate-700 rounded-2xl p-5 text-center">
              <div className="w-14 h-14 rounded-2xl bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                <FileText className="w-7 h-7 text-violet-400" />
              </div>
              <h2 className="text-lg font-bold text-white mb-1">{exam.exam?.title || `${subject} Mock Exam`}</h2>
              <p className="text-sm text-slate-400 mb-3">{className} • CBSE Pattern</p>
              <div className="flex items-center justify-center gap-6">
                <div className="flex items-center gap-1.5 text-sm">
                  <Award className="w-4 h-4 text-amber-400" />
                  <span className="text-white font-medium">{exam.exam?.total_marks || 65}</span>
                  <span className="text-slate-500">marks</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <Clock className="w-4 h-4 text-cyan-400" />
                  <span className="text-white font-medium">{exam.exam?.duration_minutes || 90}</span>
                  <span className="text-slate-500">mins</span>
                </div>
              </div>
            </div>

            {/* Regeneration Counter */}
            <div className={`flex items-center justify-between p-3 rounded-xl ${
              remainingRegenerations > 3 
                ? 'bg-emerald-500/10 border border-emerald-500/20' 
                : remainingRegenerations > 0 
                  ? 'bg-amber-500/10 border border-amber-500/20' 
                  : 'bg-red-500/10 border border-red-500/20'
            }`}>
              <div className="flex items-center gap-2">
                <RefreshCw className={`w-4 h-4 ${
                  remainingRegenerations > 3 ? 'text-emerald-400' : 
                  remainingRegenerations > 0 ? 'text-amber-400' : 'text-red-400'
                }`} />
                <span className="text-sm text-slate-300">Regenerations</span>
              </div>
              <span className={`text-sm font-bold ${
                remainingRegenerations > 3 ? 'text-emerald-400' : 
                remainingRegenerations > 0 ? 'text-amber-400' : 'text-red-400'
              }`}>
                {remainingRegenerations} left
              </span>
            </div>

            {/* Sections */}
            {exam.exam?.sections?.map((section, sIdx) => (
              <div key={sIdx} className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden" data-testid={`mock-section-${sIdx}`}>
                <button
                  onClick={() => setExpandedSection(expandedSection === sIdx ? null : sIdx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-800/30 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      section.name?.includes('MCQ') ? 'bg-violet-500/20' :
                      section.name?.includes('Short') ? 'bg-cyan-500/20' : 'bg-amber-500/20'
                    }`}>
                      <span className={`text-sm font-bold ${
                        section.name?.includes('MCQ') ? 'text-violet-400' :
                        section.name?.includes('Short') ? 'text-cyan-400' : 'text-amber-400'
                      }`}>
                        {section.questions?.length}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">{section.name}</h3>
                      <p className="text-xs text-slate-500">{section.marks_each} mark(s) each</p>
                    </div>
                  </div>
                  {expandedSection === sIdx 
                    ? <ChevronUp className="w-5 h-5 text-slate-500" /> 
                    : <ChevronDown className="w-5 h-5 text-slate-500" />
                  }
                </button>

                <AnimatePresence>
                  {expandedSection === sIdx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-800"
                    >
                      <div className="p-4 space-y-3">
                        {section.questions?.map((q, qIdx) => (
                          <div key={qIdx} className="bg-slate-800/30 rounded-xl p-4" data-testid={`mock-q-${sIdx}-${qIdx}`}>
                            <p className="text-sm text-white mb-3">
                              <span className="text-violet-400 font-semibold">Q{qIdx + 1}.</span> {q.question}
                              <span className="text-[10px] text-slate-600 ml-2">[{q.marks}m]</span>
                            </p>

                            {/* MCQ options */}
                            {q.options && (
                              <div className="grid grid-cols-1 gap-2">
                                {q.options.map((opt, oIdx) => (
                                  <button
                                    key={oIdx}
                                    onClick={() => setMcqAnswers(prev => ({ ...prev, [`${sIdx}-${qIdx}`]: oIdx }))}
                                    className={`text-sm p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                                      mcqAnswers[`${sIdx}-${qIdx}`] === oIdx
                                        ? oIdx === q.correct
                                          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                                          : 'border-red-500 bg-red-500/10 text-red-300'
                                        : 'border-slate-700 text-slate-400 hover:border-slate-600'
                                    }`}
                                  >
                                    <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                                      mcqAnswers[`${sIdx}-${qIdx}`] === oIdx
                                        ? oIdx === q.correct ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                                        : 'bg-slate-700 text-slate-400'
                                    }`}>
                                      {String.fromCharCode(65 + oIdx)}
                                    </div>
                                    {opt}
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* Show answer toggle for short/long */}
                            {q.expected_answer && (
                              <>
                                <button
                                  onClick={() => setExpandedQ(expandedQ === `${sIdx}-${qIdx}` ? null : `${sIdx}-${qIdx}`)}
                                  className="text-xs text-violet-400 mt-3 hover:text-violet-300 font-medium"
                                >
                                  {expandedQ === `${sIdx}-${qIdx}` ? '▲ Hide Answer' : '▼ Show Expected Answer'}
                                </button>
                                <AnimatePresence>
                                  {expandedQ === `${sIdx}-${qIdx}` && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      className="mt-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3"
                                    >
                                      <p className="text-xs text-emerald-300 whitespace-pre-wrap">{q.expected_answer}</p>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                data-testid="refresh-mock-btn"
                onClick={() => generateExam(true)}
                disabled={generating || remainingRegenerations === 0}
                className="flex-1 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl py-3 text-sm font-medium hover:bg-slate-700 flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Regenerate
              </button>
              <button
                data-testid="new-mock-btn"
                onClick={startNewExam}
                className="flex-1 bg-violet-600 hover:bg-violet-500 text-white rounded-xl py-3 text-sm font-medium transition-all"
              >
                New Exam
              </button>
            </div>

            {/* XP Earned */}
            <div className="bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/20 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold text-violet-300">+40 XP</span>
                </div>
                <div className="w-px h-4 bg-slate-700" />
                <div className="flex items-center gap-1.5">
                  <span className="text-amber-400">🪙</span>
                  <span className="text-sm font-semibold text-amber-300">+20 Coins</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
